/* import __COLOCATED_TEMPLATE__ from './loading-error.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

export interface StructuredError {
  code?: string; // 'query_timeout' | 'rate_limit_exceeded' | 'unknown'
  type?: string; // 'service_unavailable' | 'too_many_requests' | 'unknown'
  message?: string;
  helpUrl?: string;
  retryAfter?: number; // seconds
}

interface Args {
  title: string;
  text: string;
  retry: () => void;
  error?: StructuredError;
  onEditView?: () => void;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
}

const RATE_LIMIT_COUNTDOWN_SECONDS = 300; // 5 minutes

export default class LoadingError extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked private countdownSeconds = 0;
  @tracked private isCountingDown = false;

  private countdownInterval?: ReturnType<typeof setInterval>;

  private get isFeatureEnabled(): boolean {
    return true;
  }

  get errorCode(): string {
    return this.args.error?.code ?? 'unknown';
  }

  get isQueryTimeout(): boolean {
    return this.isFeatureEnabled && this.errorCode === 'query_timeout';
  }

  get isRateLimitExceeded(): boolean {
    return this.isFeatureEnabled && this.errorCode === 'rate_limit_exceeded';
  }

  get isMaxRegexPredicatesExceeded(): boolean {
    return this.errorCode === 'max_regex_predicates_exceeded';
  }

  get errorHeading(): string {
    if (this.isQueryTimeout || this.isRateLimitExceeded || this.isMaxRegexPredicatesExceeded) {
      return this.intl.t('inbox.conversations-table.errors.unable-to-load');
    }
    return this.args.title;
  }

  get errorBody(): string {
    if (this.isQueryTimeout) {
      return this.intl.t('inbox.conversations-table.errors.timeout-text');
    }
    if (this.isRateLimitExceeded) {
      return this.intl.t('inbox.conversations-table.errors.rate-limit-text');
    }
    if (this.isMaxRegexPredicatesExceeded) {
      return this.intl.t('inbox.conversations-table.errors.max-regex-predicates-text');
    }
    return this.args.text;
  }

  get helpUrl(): string {
    return (
      this.args.error?.helpUrl ??
      'https://www.intercom.com/help/en/articles/6588834-organize-your-inbox-with-custom-views-and-folders#h_e96fb4793a'
    );
  }

  get countdownDisplay(): string {
    let minutes = Math.floor(this.countdownSeconds / 60);
    let seconds = this.countdownSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get showCountdown(): boolean {
    return this.isCountingDown && this.countdownSeconds > 0;
  }

  @action startCountdown(): void {
    if (this.isCountingDown) {
      return;
    }

    this.countdownSeconds = this.args.error?.retryAfter ?? RATE_LIMIT_COUNTDOWN_SECONDS;
    this.isCountingDown = true;

    this.countdownInterval = setInterval(() => {
      this.countdownSeconds--;
      if (this.countdownSeconds <= 0) {
        this.clearCountdownInterval();
        this.isCountingDown = false;
      }
    }, 1000);
  }

  @action handleRetry(): void {
    this.clearCountdownInterval();
    this.isCountingDown = false;
    this.countdownSeconds = 0;
    this.args.retry();
  }

  private clearCountdownInterval(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }

  willDestroy(): void {
    super.willDestroy();
    this.clearCountdownInterval();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationsTable::LoadingError': typeof LoadingError;
    'inbox2/conversations-table/loading-error': typeof LoadingError;
  }
}
